<template>
  <div ref="root" class="marquee" :class="[`marquee--${variant}`]">
    <div ref="track" class="marquee__track">
      <div
        v-for="copy in copies"
        :key="copy"
        ref="sets"
        class="marquee__set"
        :aria-hidden="copy > 1 ? 'true' : null"
      >
        <template v-for="(item, i) in items">
          <span :key="`t-${i}`" class="marquee__item">{{ item }}</span>
          <span :key="`s-${i}`" class="marquee__sep" aria-hidden="true">✺</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/animations/gsap'
import { Marquee } from '@/animations/marquee'

/**
 * Cinta de texto infinita. Cambia de sentido según la dirección del scroll
 * y se acelera con la velocidad del scroll (inspirado en el footer de Picannte).
 */
export default {
  name: 'MarqueeText',
  props: {
    items: { type: Array, required: true },
    speed: { type: Number, default: 70 },
    direction: { type: Number, default: -1 },
    variant: { type: String, default: 'plain' }
  },
  data() {
    return { copies: 2 }
  },
  mounted() {
    if (prefersReducedMotion()) return
    this.marquee = new Marquee(this.$refs.track, this.direction, this.speed)
    this.sense = 1
    this.boost = 0
    this.visible = false

    this.measure()
    this.resizeObserver = new ResizeObserver(() => this.measure())
    this.resizeObserver.observe(this.$refs.root)

    this.trigger = ScrollTrigger.create({
      trigger: this.$refs.root,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: (self) => { this.visible = self.isActive },
      onUpdate: (self) => {
        this.sense = self.direction
        this.boost = Math.min(Math.abs(self.getVelocity()) * 0.25, 900)
      }
    })

    this.tick = (_time, deltaMs) => {
      if (!this.visible) return
      const dt = Math.min(deltaMs / 1000, 0.05)
      this.boost *= Math.pow(0.02, dt)
      this.marquee.tick(dt, this.sense, this.boost)
    }
    gsap.ticker.add(this.tick)
  },
  beforeDestroy() {
    if (this.tick) gsap.ticker.remove(this.tick)
    if (this.trigger) this.trigger.kill()
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    measure() {
      const set = this.$refs.sets && this.$refs.sets[0]
      if (!set) return
      const width = set.getBoundingClientRect().width
      if (!width) return
      const needed = Math.ceil((window.innerWidth * 1.25) / width) + 1
      if (needed !== this.copies) this.copies = needed
      this.marquee.resize(width)
    }
  }
}
</script>

<style scoped>
.marquee {
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
}

.marquee__track {
  display: flex;
  width: max-content;
  will-change: transform;
}

.marquee__set {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.marquee__item,
.marquee__sep {
  padding-right: 0.45em;
}

.marquee__sep {
  font-size: 0.55em;
  color: var(--c-accent);
}

/* Cinta roja de marca */
.marquee--band {
  background: var(--c-accent);
  color: #fff;
  padding: 0.35em 0 0.2em;
}

.marquee--band .marquee__sep {
  color: #000;
}

/* Texto calado (solo contorno) */
.marquee--outline .marquee__item {
  color: transparent;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.85);
}
</style>
